<template>
  <!-- HelpBar -->
  <HelpBar />

  <v-card class="project-card" elevation="2">
    <!-- Header / Toolbar -->
    <v-toolbar flat class="project-toolbar">
      <div class="project-toolbar-content">
        <div class="project-title-block">
          <div class="project-title-icon">
            <v-icon color="primary" size="28">mdi-folder-multiple</v-icon>
          </div>

          <div>
            <div class="text-h6 font-weight-bold">
              Projekte
            </div>
            <div class="text-body-2 text-medium-emphasis">
              Verwalten, durchsuchen und öffnen Sie Ihre Projekte.
            </div>
          </div>
        </div>

        <div class="project-toolbar-actions">
          <!-- Search -->
          <v-text-field
            v-model="search"
            density="comfortable"
            class="search-field"
            variant="outlined"
            clearable
            prepend-inner-icon="mdi-magnify"
            label="Suchen"
            hide-details
          />

          <!-- Help button -->
          <v-tooltip text="Hilfe anzeigen" location="bottom" :disabled="store.sidebarShown" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                @click="store.sidebarShown ? store.hideSidebar() : store.showSidebar(ProjectListHelp)"
                color="primary"
                variant="flat"
                class="help-button"
              >
                <v-icon v-if="!store.sidebarShown" start>
                  mdi-help-circle-outline
                </v-icon>
                <v-icon v-else start>
                  mdi-close
                </v-icon>
                Hilfe
              </v-btn>
            </template>
          </v-tooltip>

          <v-btn
            color="primary"
            variant="elevated"
            class="primary-create-button"
            @click="newProject"
          >
            <v-icon start>mdi-plus</v-icon>
            Neues Projekt
          </v-btn>
        </div>
      </div>
    </v-toolbar>

    <v-divider />

    <v-card-text class="project-card-body">
      <!-- Controls -->
      <v-sheet class="project-controls" elevation="0">
        <div class="project-controls-left">
          <v-select
            v-model="sortBy"
            :items="sortOptions"
            label="Sortieren nach"
            density="comfortable"
            variant="outlined"
            hide-details
            class="sort-select"
          />

          <v-tooltip :text="sortDesc ? 'Absteigend' : 'Aufsteigend'" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                :icon="sortDesc ? 'mdi-sort-descending' : 'mdi-sort-ascending'"
                variant="tonal"
                color="primary"
                @click="sortDesc = !sortDesc"
              />
            </template>
          </v-tooltip>
        </div>

        <div class="project-controls-right">
          <v-chip color="primary" variant="tonal" class="font-weight-medium">
            <v-icon start>mdi-folder-outline</v-icon>
            {{ filteredAndSorted.length }} Projekt{{ filteredAndSorted.length === 1 ? '' : 'e' }}
          </v-chip>
        </div>
      </v-sheet>

      <!-- Loading state -->
      <div v-if="loading" class="mt-4">
        <v-skeleton-loader
          type="table"
          class="rounded-xl"
          elevation="1"
        />
      </div>

      <!-- Empty state -->
      <div v-else-if="filteredAndSorted.length === 0" class="empty-state">
        <v-sheet class="empty-state-sheet pa-8 text-center">
          <div class="empty-icon">
            <v-icon size="58" color="primary">mdi-folder-search-outline</v-icon>
          </div>

          <div class="text-h6 font-weight-bold mb-1">
            Keine Projekte gefunden
          </div>

          <div class="text-medium-emphasis mb-5">
            Erstellen Sie ein neues Projekt oder passen Sie Ihre Suche an.
          </div>

          <v-btn color="primary" @click="newProject">
            <v-icon start>mdi-plus</v-icon>
            Neues Projekt
          </v-btn>
        </v-sheet>
      </div>

      <!-- Table -->
      <div v-else class="table-scroll">
        <v-table class="project-table" density="comfortable" hover>
          <thead>
            <tr>
              <th class="text-left sticky-header">Name</th>
              <th class="text-right sticky-header">Erstellt</th>
              <th class="text-right sticky-header">Zuletzt bearbeitet</th>
              <th class="text-right sticky-header">Gesendet von</th>
              <th class="text-right sticky-header">Bemerkung</th>
              <th class="text-right sticky-header">Option</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="item in filteredAndSorted"
              :key="item.id"
              @click="onClick(item.id)"
              class="pointer row-hover"
              tabindex="0"
              @keydown.enter="onClick(item.id)"
            >
              <td>
                <div class="project-name-cell">
                  <v-avatar size="34" class="mr-3" color="primary" variant="tonal">
                    <v-icon size="19" color="primary">mdi-file-tree</v-icon>
                  </v-avatar>

                  <div class="project-name-text">
                    <div class="text-body-1 font-medium text-truncate">
                      {{ item.name }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      Projekt-ID: {{ item.id }}
                    </div>
                  </div>
                </div>
              </td>

              <td class="text-right text-no-wrap">
                {{ formatDate(item.created_at) }}
              </td>

              <td class="text-right text-no-wrap">
                {{ formatDateTime(item.updated_at) }}
              </td>

              <td class="text-right text-truncate">
                {{ item.created_by_email || '-' }}
              </td>

              <td class="text-right text-truncate">
                {{ item.note || '-' }}
              </td>

              <td class="text-right" @click.stop>
                <div class="project-actions">
                    <!-- Rename -->
                      <v-dialog v-model="dialogRename" persistent max-width="520px">
                        <template #activator="{ props: aprops }">
                          <v-tooltip text="Umbenennen" location="bottom" color="surface" content-class="tooltip">
                            <template #activator="{ props: tooltipProps }">
                              <v-btn
                                v-bind="{ ...aprops, ...tooltipProps }"
                                icon
                                variant="text"
                                color="primary"
                                class="action-icon-button"
                                aria-label="Umbenennen"
                                @click="startRename(item)"
                              >
                                <v-icon>mdi-rename-outline</v-icon>
                              </v-btn>
                            </template>
                          </v-tooltip>
                        </template>

                        <v-card>
                          <v-card-title class="d-flex align-center">
                            <v-icon class="mr-2" color="primary">mdi-rename-outline</v-icon>
                            <span class="text-h6">Projekt umbenennen</span>
                          </v-card-title>

                          <v-card-text>
                            <v-text-field
                              label="Neuer Name"
                              v-model="newProjectName"
                              autofocus
                              :rules="[v => !!v || 'Name ist erforderlich']"
                              @keyup.enter="renameProject(dialogId)"
                            />
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer />
                            <v-btn color="red" variant="text" @click="cancelRename">
                              Abbrechen
                            </v-btn>
                            <v-btn
                              color="primary"
                              variant="flat"
                              @click="renameProject(dialogId)"
                              :disabled="!newProjectName"
                            >
                              Speichern
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>

                    <!-- Copy -->
                    <v-tooltip text="Kopie erstellen" location="bottom" color="surface" content-class="tooltip">
                      <template #activator="{ props: tooltipProps }">
                        <v-btn
                          v-bind="tooltipProps"
                          icon
                          variant="text"
                          color="primary"
                          class="action-icon-button"
                          aria-label="Kopie erstellen"
                          @click="copyProject(item.id)"
                        >
                          <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>

                    <!-- Note -->
                      <v-dialog v-model="dialogNote" persistent max-width="520px">
                        <template #activator="{ props: nprops }">
                          <v-tooltip text="Bemerkung bearbeiten" location="bottom" color="surface" content-class="tooltip">
                            <template #activator="{ props: tooltipProps }">
                              <v-btn
                                v-bind="{ ...nprops, ...tooltipProps }"
                                icon
                                variant="text"
                                color="primary"
                                class="action-icon-button"
                                aria-label="Bemerkung bearbeiten"
                                @click="openNoteDialog(item)"
                              >
                                <v-icon>mdi-note-edit-outline</v-icon>
                              </v-btn>
                            </template>
                          </v-tooltip>
                        </template>

                        <v-card>
                          <v-card-title class="d-flex align-center">
                            <v-icon class="mr-2" color="primary">mdi-note-edit-outline</v-icon>
                            <span class="text-h6">Bemerkung bearbeiten</span>
                          </v-card-title>

                          <v-card-text>
                            <v-textarea
                              label="Bemerkung"
                              v-model="newProjectNote"
                              rows="4"
                              auto-grow
                            />
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer />
                            <v-btn
                              color="red"
                              variant="text"
                              @click="
                                dialogNote = false;
                                newProjectNote = null;
                              "
                            >
                              Abbrechen
                            </v-btn>
                            <v-btn
                              color="primary"
                              variant="flat"
                              @click="updateProjectNote()"
                            >
                              Speichern
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>

                    <!-- Send project -->
                    <v-tooltip text="Projektkopie senden" location="bottom" color="surface" content-class="tooltip">
                      <template #activator="{ props: tooltipProps }">
                        <v-btn
                          v-bind="tooltipProps"
                          icon
                          variant="text"
                          color="primary"
                          class="action-icon-button"
                          aria-label="Projektkopie senden"
                          @click="shareProject(item.id)"
                        >
                          <v-icon>mdi-share-variant-outline</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>

                    <!-- Delete -->
                      <v-dialog v-model="dialogDelete" persistent max-width="420">
                        <template #activator="{ props: dprops }">
                          <v-tooltip text="Löschen" location="bottom" color="surface" content-class="tooltip">
                            <template #activator="{ props: tooltipProps }">
                              <v-btn
                                v-bind="{ ...dprops, ...tooltipProps }"
                                icon
                                variant="text"
                                color="red"
                                class="action-icon-button action-delete-button"
                                aria-label="Löschen"
                                @click="storeIdForDialog(item.id)"
                              >
                                <v-icon>mdi-delete-outline</v-icon>
                              </v-btn>
                            </template>
                          </v-tooltip>
                        </template>

                        <v-card>
                          <v-card-title class="d-flex align-center">
                            <v-icon class="mr-2" color="red">mdi-alert</v-icon>
                            <span class="text-h6">Projekt löschen?</span>
                          </v-card-title>

                          <v-card-text>
                            Diese Aktion kann nicht rückgängig gemacht werden.
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer />
                            <v-btn variant="text" @click="dialogDelete = false">
                              Abbrechen
                            </v-btn>
                            <v-btn color="red" variant="flat" @click="confirmDelete">
                              Löschen
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>
                </div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>

      <!-- Bottom actions -->
      <div class="project-bottom-actions">
        <!--
          Ein Knopf statt eines je Demoart. Mit jeder weiteren Demo wuchs die
          Leiste, und die Namen „Demo" und „Demo2" sagten nicht, was einen
          erwartet. Die Auswahl steht jetzt im Dialog, mit je einem Satz dazu.
        -->
        <v-btn
          color="secondary"
          class="new-button"
          variant="tonal"
          @click="demoDialog = true"
        >
          <v-icon start>mdi-flask</v-icon>
          Demo-Projekt anlegen
        </v-btn>

        <v-dialog v-model="demoDialog" max-width="640">
          <v-card>
            <v-card-title class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-flask</v-icon>
              Demo-Projekt anlegen
            </v-card-title>

            <v-card-text>
              <p class="text-body-2 text-medium-emphasis mb-3">
                Demo-Projekte legen ein vollstaendig ausgefuelltes Beispielprojekt in Ihrem
                Konto an. Sie koennen es gefahrlos veraendern oder loeschen.
              </p>

              <v-radio-group v-model="demoKind" density="comfortable" hide-details>
                <v-radio
                  v-for="demo in DEMO_KINDS"
                  :key="demo.value"
                  :value="demo.value"
                >
                  <template #label>
                    <div>
                      <div class="font-weight-medium">
                        <v-icon size="18" class="mr-1">{{ demo.icon }}</v-icon>
                        {{ demo.title }}
                      </div>
                      <div class="text-caption text-medium-emphasis">{{ demo.description }}</div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="demoDialog = false">Abbrechen</v-btn>
              <v-btn
                color="primary"
                variant="elevated"
                :loading="creatingDemo"
                :disabled="creatingDemo"
                @click="createSelectedDemo"
              >
                <v-icon start>mdi-plus</v-icon>
                Anlegen
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-btn
          color="primary"
          @click="newProject"
          class="new-button"
          variant="elevated"
        >
          <v-icon start>mdi-plus</v-icon>
          Neues Projekt
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/plugins/axios';
import { toast } from 'vue3-toastify';
import { useProjectStore } from '@/stores/projectStore';
import HelpBar from '@/components/HelpBar.vue';
import ProjectListHelp from '@/components/help/ProjectListHelp.vue';

const router = useRouter();
const store = useProjectStore();

const dialogDelete = ref(false);
const dialogRename = ref(false);
const newProjectName = ref(null);
const dialogId = ref(null);

const dialogNote = ref(false);
const newProjectNote = ref(null);
const noteProjectId = ref(null);

/*
 * Die waehlbaren Demo-Projekte. Eine neue Demo braucht hier einen Eintrag und
 * im Backend einen Endpunkt — sonst nichts.
 */
const DEMO_KINDS = [
  {
    value: "demo",
    title: "Demo — Einstieg",
    description: "Kleines Beispielprojekt mit wenigen Massnahmen. Gut zum ersten Umsehen.",
    icon: "mdi-flask",
    endpoint: "/projects/create_demo/",
  },
  {
    value: "demo2",
    title: "Demo 2 — alle Massnahmenarten",
    description: "Je ein Beispiel fuer jedes Rechenverfahren: Beleuchtung, Pumpen, Ventilatoren, Kessel, Kaelte, Antriebe, PV und mehr.",
    icon: "mdi-flask-outline",
    endpoint: "/projects/create_demo2/",
  },
  {
    value: "valeri",
    title: "Demo VALERI — Wirtschaftlichkeit",
    description: "Massnahmenpaket mit unterschiedlichen Nutzungsdauern und Amortisationen, abgestimmt auf die Bewertung nach DIN EN 17463.",
    icon: "mdi-calculator-variant",
    endpoint: "/projects/create_demo_valeri/",
  },
];

const demoDialog = ref(false);
const demoKind = ref("valeri");
const creatingDemo = ref(false);

const loading = ref(true);
const search = ref('');
const sortBy = ref('created_at');
const sortDesc = ref(true);

const sortOptions = [
  { title: 'Erstellt', value: 'created_at' },
  { title: 'Name', value: 'name' },
];

const projects = computed(() => Object.values(store.allProjects || {}));

onMounted(async () => {
  await store.fetchAllProjects();
  loading.value = false;
  store.setHelpComponent(ProjectListHelp);
});

function onClick(projectId) {
  router.push({ name: 'ProjectDashboard', params: { projectId } });
}

function newProject() {
  // Navigate or open new project dialog if you have one
  router.push({ name: 'NewProject' });
}

function storeIdForDialog(id) {
  dialogId.value = id;
}

function startRename(item) {
  dialogId.value = item.id;
  newProjectName.value = item.name;
  dialogRename.value = true;
}

async function renameProject(projectId) {
  if (!projectId || !newProjectName.value) return;
  try {
    await api.patch(`/projects/${projectId}/`, {
      name: newProjectName.value,
    });
    dialogRename.value = false;
    newProjectName.value = null;
    await store.fetchAllProjects();
  } catch (error) {
    console.error('Projekt kann nicht umbenannt werden.', error);
  }
}

function cancelRename() {
  dialogRename.value = false;
  newProjectName.value = null;
}

async function copyProject(id) {
  try {
    await api.post(`/projects/${id}/duplicate/`, {
      projectId: id,
    });

    // Toast success
    toast.success("Projekt erfolgreich kopiert.");

    // Refresh project list
    await store.fetchAllProjects();

  } catch (error) {
    console.error("Copy project error:", error);

    const errorData = error.response?.data;

    const isDraftActionError =
      errorData?.code === "DRAFT_ACTION_EXISTS" ||
      errorData?.error?.includes("DRAFT_ACTION_EXISTS");

    
    if (isDraftActionError) {
        toast.warning("Projekt hat eine Draft-Action und kann nicht kopiert werden.");
    } else {
      toast.error("Projekt kann nicht kopiert werden.");
    }

  }

}

function shareProject(id) {
  router.push({ name: 'ShareProject', params: { projectId: id } });
}

async function confirmDelete() {
  
  if (!dialogId.value) return;

    try {
      await api.delete(`/projects/${dialogId.value}/`);

      toast.success(`Projekt erfolgreich gelöscht.`);

      dialogDelete.value = false;

      // Refresh the project list
      await store.fetchAllProjects();

    } catch (error) {
      console.error("Projekt kann nicht gelöscht werden:", error);
      toast.error("Es gab einen Fehler beim Löschen des Projekts.");
    }

}


function formatDate(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatDateTime(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}


// function formatDate(date) {
//   if (!date) return '';
//   try {
//     return new Date(date).toLocaleDateString();
//   } catch {
//     return String(date);
//   }
// }

/*
 * Eine Funktion fuer alle Demoarten: der gewaehlte Eintrag bringt seinen
 * Endpunkt mit. Vorher stand je Demo eine eigene, bis auf die URL gleiche
 * Funktion samt eigenem Ladezustand hier.
 */
async function createSelectedDemo() {
  if (creatingDemo.value) return;
  const demo = DEMO_KINDS.find((eintrag) => eintrag.value === demoKind.value);
  if (!demo) return;

  creatingDemo.value = true;
  try {
    await api.post(demo.endpoint);
    toast.success(`${demo.title} erfolgreich erstellt.`);
    demoDialog.value = false;
    await store.fetchAllProjects();
  } catch (error) {
    console.error(`${demo.title} kann nicht erstellt werden:`, error);
    toast.error("Es gab einen Fehler beim Erstellen des Demo-Projekts.");
  } finally {
    creatingDemo.value = false;
  }
}


function openNoteDialog(item) {
  // Implement note dialog logic here
  noteProjectId.value = item.id;
  newProjectNote.value = item.note || '';
  dialogNote.value = true;
}

async function updateProjectNote() {
  if (!noteProjectId.value) return;
  try {
    await api.patch(`/projects/${noteProjectId.value}/`, {
      note: newProjectNote.value,
    });
    toast.success("Projektnotiz erfolgreich aktualisiert.");
    dialogNote.value = false;
    newProjectNote.value = null;
    noteProjectId.value = null;
    await store.fetchAllProjects();
  } catch (error) {
    console.error('Projektnotiz kann nicht aktualisiert werden.', error);
    toast.error("Es gab einen Fehler beim Aktualisieren der Projektnotiz.");
  }
} 


const filteredAndSorted = computed(() => {
  const q = (search.value || '').toLowerCase().trim();
  let list = projects.value;

  if (q) {
    list = list.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.id + '').includes(q)
    );
  }

  const key = sortBy.value;
  const dir = sortDesc.value ? -1 : 1;

  return [...list].sort((a, b) => {
    const av = a?.[key];
    const bv = b?.[key];

    if (key === 'created_at') {
      const ad = new Date(av).getTime() || 0
      const bd = new Date(bv).getTime() || 0
      return (ad - bd) * dir
    }

    // string/other sort
    const as = (av ?? '').toString().toLowerCase();
    const bs = (bv ?? '').toString().toLowerCase();
    return as < bs ? -dir : as > bs ? dir : 0;
  });
});
</script>

<style scoped lang="scss">
.project-card {
  overflow: hidden;
  border-radius: 28px;
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.project-toolbar {
  min-height: auto;
  background:
    linear-gradient(
      180deg,
      rgba(var(--v-theme-primary), 0.07),
      rgba(var(--v-theme-primary), 0.025)
    );
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.project-toolbar-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
}

.project-title-block {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 220px;
}

.project-title-icon {
  width: 48px;
  height: 48px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.project-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.search-field {
  width: min(360px, 40vw);
}

.search-field :deep(.v-field),
.sort-select :deep(.v-field) {
  border-radius: 18px;
}

.help-button,
.primary-create-button,
.new-button {
  text-transform: none;
  font-weight: 600;
}

.project-card-body {
  padding: 1.25rem;
}

.project-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem;
  margin-bottom: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.035);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.project-controls-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.project-controls-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.sort-select {
  width: 230px;
}

.table-scroll {
  max-height: 60vh;
  overflow: auto;
  border-radius: 22px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background-color: rgb(var(--v-theme-surface));
}

.project-table {
  background-color: transparent;
}

.project-table thead tr th.sticky-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
  box-shadow: 0 1px 0 rgba(var(--v-theme-on-surface), 0.08);
}

.project-table tbody tr:nth-child(odd) {
  background-color: rgba(var(--v-theme-primary), 0.018);
}

.project-table tbody tr.row-hover {
  transition:
    background-color 160ms ease,
    box-shadow 160ms ease;
}

.project-table tbody tr.row-hover:hover {
  background-color: rgba(var(--v-theme-primary), 0.075);
}

.project-table tbody tr.row-hover:focus-visible {
  outline: 2px solid rgba(var(--v-theme-primary), 0.45);
  outline-offset: -2px;
}

.project-name-cell {
  display: flex;
  align-items: center;
  min-width: 260px;
}

.project-name-text {
  min-width: 0;
}

.pointer {
  cursor: pointer;
}

/* Inline action row, matching the Maßnahmen options on the project dashboard. */
.project-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}

.action-icon-button {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 999px;
}

/*
 * Vuetify sizes icon glyphs relative to the button font-size (~21px at the
 * default button size). Pin it explicitly so the row does not read as heavier
 * than the Maßnahmen options on the project dashboard.
 */
.action-icon-button .v-icon {
  font-size: 18px;
}

.action-icon-button:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.action-delete-button {
  color: rgb(var(--v-theme-error));
}

.action-delete-button:hover {
  background-color: rgba(var(--v-theme-error), 0.08);
}

.empty-state {
  display: grid;
  place-items: center;
  min-height: 260px;
}

.empty-state-sheet {
  width: min(100%, 560px);
  border-radius: 28px;
  background-color: rgba(var(--v-theme-primary), 0.035);
  border: 1px solid rgba(var(--v-theme-primary), 0.08);
}

.empty-icon {
  width: 82px;
  height: 82px;
  margin: 0 auto 1rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.project-bottom-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.new-button {
  border-radius: 999px;
}

.font-medium {
  font-weight: 550;
}

.text-truncate {
  max-width: 520px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td),
:deep(.v-table .v-table__wrapper > table > thead > tr > th) {
  padding-top: 12px;
  padding-bottom: 12px;
}

@media (max-width: 1100px) {
  .project-toolbar-content {
    align-items: stretch;
    flex-direction: column;
  }

  .project-toolbar-actions {
    justify-content: flex-start;
  }

  .search-field {
    width: 100%;
  }

  .project-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .project-controls-left {
    flex-wrap: wrap;
  }

  .project-controls-right {
    justify-content: flex-start;
  }
}

@media (max-width: 700px) {
  .project-card-body {
    padding: 1rem;
  }

  .sort-select {
    width: 100%;
  }

  .project-controls-left {
    align-items: stretch;
    flex-direction: column;
  }

  .project-bottom-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .project-bottom-actions .v-btn {
    width: 100%;
  }

  .primary-create-button,
  .help-button {
    width: 100%;
  }
}
</style>